import React, { useState, useEffect } from 'react';
import { X, Bell, Eye, EyeOff, Wallet, Gift, User, BellRing, Lock, Link2, Copy, LogOut, ShieldCheck, ArrowDownToLine, ArrowUpFromLine, Check, Loader2, Plus, ExternalLink, Twitter, Send, Youtube, Crown, Receipt, ChevronRight, Trophy, Gamepad2 } from 'lucide-react';
import { useLang } from '../LangContext';
import { useAuth } from '../AuthContext';
import { airdropApi, authApi, referralApi, gameApi } from '../api';
import { useToast } from '../hooks/use-toast';
import AdminPage from './AdminPage';
import WithdrawModal from './WithdrawModal';
import PhantomDepositModal from './PhantomDepositModal';
import usePhantom from '../hooks/usePhantom';

const Row = ({ icon: Icon, label, right, onClick, danger }) => (
  <button onClick={onClick} className={`w-full flex items-center justify-between py-3.5 px-1 border-t border-slate-100 hover:bg-slate-50 transition group ${danger ? 'text-red-600' : 'text-slate-800'}`}>
    <div className="flex items-center gap-3">
      <Icon className={`w-5 h-5 ${danger ? 'text-red-500' : 'text-slate-500 group-hover:text-blue-600'}`} />
      <span className="text-[14px] font-semibold">{label}</span>
    </div>
    {right && <div className="text-[13px] font-bold text-blue-600">{right}</div>}
  </button>
);

const TASK_ICONS = {
  youtube: { icon: Youtube, color: 'bg-red-600', label: 'YouTube' },
  telegram: { icon: Send, color: 'bg-sky-500', label: 'Telegram' },
  twitter: { icon: Twitter, color: 'bg-slate-900', label: 'X (Twitter)' },
  coinmarketcap: { icon: ExternalLink, color: 'bg-blue-700', label: 'CoinMarketCap' },
  coingecko: { icon: ExternalLink, color: 'bg-emerald-600', label: 'CoinGecko' },
};

const Dashboard = ({ open, onClose }) => {
  const { t } = useLang();
  const { user, logout, refresh } = useAuth();
  const { toast } = useToast();
  const [showBal, setShowBal] = useState(true);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState('main'); // main | airdrop | linkWallet | profile | history | gameHistory | referral
  const [walletInput, setWalletInput] = useState('');
  const [airdropInfo, setAirdropInfo] = useState(null);
  const [loadingTask, setLoadingTask] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(null); // which task currently shows the verify form
  const [proofInput, setProofInput] = useState('');
  const [startedAt, setStartedAt] = useState({}); // task -> timestamp
  const [, forceTick] = useState(0); // re-render every second for countdowns
  const [linking, setLinking] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [txs, setTxs] = useState(null);
  const [gameHistory, setGameHistory] = useState(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [refInfo, setRefInfo] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [refEarnings, setRefEarnings] = useState(null);
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [phantomDepositOpen, setPhantomDepositOpen] = useState(false);
  const phantom = usePhantom();

  useEffect(() => {
    if (open && view === 'referral' && !refInfo) {
      referralApi.me().then(setRefInfo).catch(() => setRefInfo(null));
      referralApi.earnings(20).then(setRefEarnings).catch(() => setRefEarnings([]));
    }
  }, [open, view, refInfo]);

  useEffect(() => { if (open && view === 'history' && !txs) authApi.myTransactions().then(setTxs).catch(() => setTxs([])); }, [open, view, txs]);
  useEffect(() => { if (open && view === 'gameHistory' && !gameHistory) gameApi.history(100).then(setGameHistory).catch(() => setGameHistory({ items: [], stats: {} })); }, [open, view, gameHistory]);
  useEffect(() => { if (view === 'profile' && user) setUsernameInput(user.username || ''); }, [view, user]);

  useEffect(() => {
    if (open && view === 'airdrop' && !airdropInfo) {
      airdropApi.info().then(setAirdropInfo).catch(() => {});
    }
  }, [open, view, airdropInfo]);

  // Tick every second so the visit-cooldown countdown updates (must be before any early return)
  useEffect(() => {
    if (!verifyOpen) return;
    const id = setInterval(() => forceTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [verifyOpen]);

  if (!open || !user) return null;

  const balance = user.walletBalance || 0;
  const airdropBalance = user.airdropBalance || 0;
  const playerId = `DA-${(user.id || '').slice(0, 6).toUpperCase()}`;

  const copyId = () => {
    navigator.clipboard.writeText(playerId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleLogout = () => {
    logout();
    onClose();
    toast({ title: 'Logged out' });
  };

  const handleLinkWallet = async () => {
    if (!walletInput.trim()) return;
    setLinking(true);
    try {
      await authApi.linkWallet(walletInput.trim());
      await refresh();
      toast({ title: 'Wallet linked', description: walletInput.trim().slice(0, 8) + '...' });
      setView('main');
      setWalletInput('');
    } catch (e) {
      toast({ title: 'Failed to link wallet', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLinking(false);
    }
  };

  const handleStartTask = async (task) => {
    setLoadingTask(task);
    const url = airdropInfo?.task_urls?.[task];
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    try {
      await airdropApi.startTask(task);
      setStartedAt((s) => ({ ...s, [task]: Date.now() }));
      setVerifyOpen(task);
      setProofInput('');
    } catch (e) {
      toast({ title: 'Could not start task', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoadingTask(null);
    }
  };

  const handleVerifyTask = async (task) => {
    setLoadingTask(task);
    try {
      const res = await airdropApi.verifyTask(task, proofInput || null);
      if (res.verified && res.reward_credited) {
        const info = await airdropApi.info();
        setAirdropInfo(info);
        await refresh();
        toast({ title: `+${res.reward_amount} DOMIA earned!`, description: res.message || `Task "${task}" verified.` });
        setVerifyOpen(null);
        setProofInput('');
        if (res.referrer_bonus_paid) {
          toast({ title: '🎁 All tasks done!', description: `Your sponsor just received +${res.referrer_bonus_paid.amount_domia} DOMIA.` });
        }
      } else if (res.verified && res.already_done) {
        toast({ title: 'Already verified', description: 'This task was already counted.' });
        setVerifyOpen(null);
      } else {
        toast({ title: 'Verification failed', description: res.message || 'Try again.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Verification failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoadingTask(null);
    }
  };

  const handleClaim = async () => {
    try {
      const res = await airdropApi.claim();
      await refresh();
      toast({ title: 'Airdrop claimed!', description: `+${res.claimed} DOMIA transferred to your balance.` });
    } catch (e) {
      toast({ title: 'Cannot claim yet', description: e?.response?.data?.detail || 'Try again after presale', variant: 'destructive' });
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-[460px] max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl border border-slate-100">
        {/* Header */}
        <div className="p-6 flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-700 to-blue-400 flex items-center justify-center text-white text-2xl font-black shadow-lg shrink-0">
            {user.username?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-black text-slate-900 truncate">{user.username}</div>
              {user.hasChallengeBadge && (
                <span
                  data-testid="challenge-badge"
                  title="Human vs AI Challenge · 3 consecutive wins!"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest text-amber-900 shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 50%, #d97706 100%)',
                    boxShadow: '0 0 10px rgba(245, 158, 11, 0.6), inset 0 0 6px rgba(255, 255, 255, 0.4)',
                    border: '1.5px solid #b45309',
                  }}
                >
                  <Trophy className="w-3 h-3" /> CHAMPION
                </span>
              )}
            </div>
            <div className="text-[13px] text-slate-500 truncate">{user.email}</div>
            <div className={`mt-1.5 flex items-center gap-1.5 text-[13px] font-bold ${user.emailVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
              <ShieldCheck className="w-4 h-4" />
              {user.emailVerified ? t.dashboard.verified : 'Unverified'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-blue-50 hover:bg-blue-100 flex items-center justify-center text-blue-600 transition">
              <Bell className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {view === 'main' && (
          <>
            {/* Balance card */}
            <div className="px-6">
              <div className="relative rounded-2xl p-5 overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 55%, #f59e0b 130%)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    <span className="text-[13px] font-bold">{t.dashboard.balance}</span>
                  </div>
                  <button onClick={() => setShowBal(v => !v)} className="flex items-center gap-1.5 text-[12px] font-bold bg-white/15 hover:bg-white/25 transition px-3 py-1.5 rounded-full">
                    {showBal ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {t.dashboard.toggle}
                  </button>
                </div>
                <div className="mt-4 text-4xl font-black tracking-tight">
                  {showBal ? `${balance.toFixed(2)} DOMIA` : '••••• DOMIA'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  onClick={() => setPhantomDepositOpen(true)}
                  data-testid="dashboard-deposit-btn"
                  className="rounded-2xl py-3.5 font-extrabold text-white text-[13px] shadow-lg hover:opacity-90"
                  style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}
                >
                  <ArrowDownToLine className="inline w-4 h-4 mr-1.5 -mt-0.5" />{t.dashboard.deposit}
                </button>
                <button
                  onClick={() => setWithdrawOpen(true)}
                  data-testid="dashboard-withdraw-btn"
                  className="rounded-2xl py-3.5 font-extrabold text-white text-[13px] shadow-lg hover:opacity-90"
                  style={{ background: 'linear-gradient(90deg, #0f172a, #1e3a8a)' }}
                >
                  <ArrowUpFromLine className="inline w-4 h-4 mr-1.5 -mt-0.5" />{t.dashboard.withdraw}
                </button>
              </div>
            </div>

            {/* Airdrop balance */}
            <div className="px-6 mt-5 pb-2 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-bold text-slate-500">{t.dashboard.airdrops}</div>
                <div className="text-2xl font-black text-slate-900 mt-0.5">{airdropBalance.toFixed(2)} DOMIA</div>
              </div>
              <button onClick={() => setView('airdrop')} className="rounded-full px-5 py-2.5 font-extrabold text-[13px] bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.4)]">
                <Gift className="inline w-4 h-4 mr-1.5 -mt-0.5" />Tasks
              </button>
            </div>

            {/* Wallet linking */}
            <div className="px-6 mt-3 pb-2">
              {user.walletAddress ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold text-emerald-700 tracking-wide">PHANTOM WALLET LINKED</div>
                    <div className="text-[12px] font-mono text-slate-700 truncate">{user.walletAddress}</div>
                  </div>
                  <button onClick={() => setView('linkWallet')} className="text-[11px] font-bold text-blue-600 shrink-0">Change</button>
                </div>
              ) : (
                <button onClick={() => setView('linkWallet')} className="w-full rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 px-3 py-3 flex items-center justify-center gap-2 text-blue-700 font-bold text-[13px]">
                  <Plus className="w-4 h-4" /> Link Phantom wallet
                </button>
              )}
            </div>

            {/* List */}
            <div className="px-6 pb-4">
              {user.isAdmin && (
                <button onClick={() => setAdminOpen(true)} className="w-full mb-3 rounded-xl px-4 py-3 flex items-center justify-between text-white font-extrabold text-[13px] shadow-[0_8px_20px_rgba(15,23,42,0.25)]" style={{ background: 'linear-gradient(90deg, #0f172a, #1e3a8a)' }}>
                  <span className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-300" /> Admin Panel</span>
                  <span className="text-amber-300">→</span>
                </button>
              )}
              <Row icon={User} label={t.dashboard.profile} onClick={() => setView('profile')} />
              <Row icon={Receipt} label="Transaction History" onClick={() => setView('history')} />
              <Row icon={Gamepad2} label="Game History (vs AI)" onClick={() => setView('gameHistory')} />
              <Row icon={BellRing} label={t.dashboard.notifications} />
              <Row icon={Lock} label={t.dashboard.security} onClick={() => setView('profile')} />
              <Row icon={Link2} label={t.dashboard.referral} onClick={() => setView('referral')} />
              <Row icon={Copy} label={t.dashboard.playerId} right={<span className="flex items-center gap-1.5">{playerId} {copied && <Check className="w-3.5 h-3.5 text-emerald-500" />}</span>} onClick={copyId} />
              <Row icon={LogOut} label={t.dashboard.logout} danger onClick={handleLogout} />
            </div>
          </>
        )}

        {view === 'linkWallet' && (
          <div className="px-6 pb-6">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Link Phantom Wallet</h3>
            <p className="text-[13px] text-slate-600 mt-1">Connect Phantom for one-click linking, or paste your Solana wallet address below.</p>

            <button
              onClick={async () => {
                try {
                  const pk = await phantom.connect();
                  setWalletInput(pk);
                } catch (e) {
                  toast({ title: 'Phantom error', description: e?.message || 'Connect failed', variant: 'destructive' });
                }
              }}
              disabled={phantom.connecting}
              data-testid="dashboard-connect-phantom-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl py-3 font-extrabold text-white text-[13px] shadow-lg disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg, #7e22ce, #6d28d9)' }}
            >
              <Wallet className="w-4 h-4" />
              {phantom.connected ? `Connected: ${phantom.publicKey.slice(0,4)}…${phantom.publicKey.slice(-4)}` : (phantom.available ? 'Connect with Phantom' : 'Install Phantom')}
            </button>

            <div className="my-3 flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              <span className="flex-1 h-px bg-slate-200" /> or paste <span className="flex-1 h-px bg-slate-200" />
            </div>

            <input
              value={walletInput}
              onChange={(e) => setWalletInput(e.target.value)}
              placeholder="e.g. 9ktBHs2Mzi2uo3XrmKrcYDnzmtkp2aitvE7v8X3TxBaF"
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-3 text-[13px] font-mono text-slate-900 focus:outline-none focus:border-blue-400"
            />
            <button onClick={handleLinkWallet} disabled={linking || !walletInput.trim()} className="mt-3 w-full rounded-full py-3 font-extrabold text-white text-[14px] shadow-lg disabled:opacity-50" style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}>
              {linking ? <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> : null}Link Wallet
            </button>
          </div>
        )}

        {view === 'airdrop' && (
          <div className="px-6 pb-6" data-testid="airdrop-view">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Airdrop Tasks</h3>
            <p className="text-[13px] text-slate-600 mt-1">
              Earn <span className="font-extrabold text-blue-700">up to 1500 DOMIA per task</span>.
              {airdropInfo?.has_referrer && (
                <> Your sponsor also gets a <span className="font-extrabold text-emerald-700">+{airdropInfo?.referrer_completion_bonus || 300} DOMIA</span> bonus once you finish all tasks.</>
              )}
            </p>
            {airdropInfo && (
              <div className="mt-3 flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5">
                <Check className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-[11px] font-extrabold text-blue-700">{airdropInfo.tasks_done || 0} / {airdropInfo.tasks_total || 5} tasks completed</span>
              </div>
            )}
            <div className="mt-4 space-y-2">
              {Object.keys(TASK_ICONS).map((task) => {
                const meta = TASK_ICONS[task];
                const done = airdropInfo?.tasks?.[task];
                const isOpen = verifyOpen === task;
                const isVisitTask = task === 'coinmarketcap' || task === 'coingecko';
                const startTs = startedAt[task];
                const elapsedSec = startTs ? Math.floor((Date.now() - startTs) / 1000) : 0;
                const cooldownRemaining = isVisitTask ? Math.max(0, 30 - elapsedSec) : 0;
                const taskReward = (airdropInfo?.task_rewards?.[task]) ?? (airdropInfo?.reward_per_task || 300);
                const isSoon = (airdropInfo?.soon_tasks || []).includes(task);
                const isWalletOnly = (airdropInfo?.wallet_only_tasks || []).includes(task);
                const proofPlaceholder = {
                  telegram: 'Your numeric Telegram ID (get it from @userinfobot)',
                  twitter: 'Your X / Twitter @username (make sure you liked, retweeted & commented)',
                  youtube: 'Your YouTube channel @handle',
                }[task];
                const proofHint = {
                  telegram: 'Open @userinfobot in Telegram → it replies with your numeric ID.',
                  twitter: 'Like, retweet AND comment on our latest pinned tweet. Then paste your @username below to auto-validate.',
                  youtube: 'Make sure your subscriptions are PUBLIC in YouTube Settings → Privacy.',
                  coinmarketcap: 'Visit the page for at least 30 seconds, then confirm.',
                  coingecko: 'Visit the page for at least 30 seconds, then confirm.',
                }[task];

                return (
                  <div key={task} className={`rounded-xl border ${done ? 'border-emerald-200 bg-emerald-50' : isSoon ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'}`} data-testid={`airdrop-task-${task}`}>
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full ${meta.color} flex items-center justify-center text-white`}><meta.icon className="w-4 h-4" /></span>
                        <div>
                          <div className="text-[13px] font-bold text-slate-900 flex items-center gap-1.5">
                            {meta.label}
                            {isSoon && <span className="text-[9px] font-black tracking-widest text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-0.5">SOON</span>}
                          </div>
                          {!done && (
                            <div className="text-[10px] font-extrabold text-blue-700">
                              +{taskReward.toLocaleString()} DOMIA
                              {isWalletOnly && <span className="ml-1 text-emerald-600">· Dashboard</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      {done ? (
                        <span className="text-[12px] font-extrabold text-emerald-600 flex items-center gap-1" data-testid={`airdrop-task-${task}-earned`}><Check className="w-3.5 h-3.5" /> Earned</span>
                      ) : isSoon ? (
                        <span className="text-[11px] font-extrabold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-1">Coming Soon</span>
                      ) : isOpen ? (
                        <button
                          onClick={() => { setVerifyOpen(null); setProofInput(''); }}
                          className="rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 text-[12px] font-extrabold"
                          data-testid={`airdrop-task-${task}-cancel`}
                        >Cancel</button>
                      ) : (
                        <button
                          onClick={() => handleStartTask(task)}
                          disabled={loadingTask === task}
                          className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-[12px] font-extrabold disabled:opacity-50"
                          data-testid={`airdrop-task-${task}-start`}
                        >
                          {loadingTask === task ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Start'}
                        </button>
                      )}
                    </div>
                    {!done && !isSoon && isOpen && (
                      <div className="border-t border-slate-200 px-3 py-3 bg-slate-50" data-testid={`airdrop-task-${task}-verify-panel`}>
                        <div className="text-[11px] text-slate-600 mb-2">{proofHint}</div>
                        {!isVisitTask && (
                          <input
                            type="text"
                            value={proofInput}
                            onChange={(e) => setProofInput(e.target.value)}
                            placeholder={proofPlaceholder}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400"
                            data-testid={`airdrop-task-${task}-proof-input`}
                          />
                        )}
                        {isVisitTask && cooldownRemaining > 0 && (
                          <div className="text-[12px] text-amber-700 font-bold">Wait {cooldownRemaining}s on the page before confirming.</div>
                        )}
                        <button
                          onClick={() => handleVerifyTask(task)}
                          disabled={loadingTask === task || (isVisitTask && cooldownRemaining > 0) || (!isVisitTask && !proofInput.trim())}
                          className="mt-2 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white py-2 text-[13px] font-extrabold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid={`airdrop-task-${task}-verify`}
                        >
                          {loadingTask === task ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Verify & Claim +{taskReward.toLocaleString()} DOMIA</>}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-amber-50 border border-blue-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold text-slate-500 tracking-wide">YOUR AIRDROP DOMIA</div>
                <div className="text-xl font-black text-slate-900">{airdropBalance.toFixed(0)} DOMIA</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Credited instantly to your wallet on each task.</div>
              </div>
              {airdropInfo?.all_tasks_completed ? (
                <span className="rounded-full bg-emerald-500 text-white px-3 py-1.5 text-[11px] font-extrabold">✓ COMPLETED</span>
              ) : (
                <span className="rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-3 py-1.5 text-[11px] font-extrabold">IN PROGRESS</span>
              )}
            </div>
          </div>
        )}

        {view === 'profile' && (
          <div className="px-6 pb-6">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Profile & Security</h3>

            <div className="mt-4">
              <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">USERNAME</label>
              <input value={usernameInput} onChange={(e) => setUsernameInput(e.target.value)} className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[14px] font-semibold text-slate-900 focus:outline-none focus:border-blue-400" />
              <button onClick={async () => {
                setSavingProfile(true);
                try { await authApi.updateProfile({ username: usernameInput }); await refresh(); toast({ title: 'Username updated' }); }
                catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
                finally { setSavingProfile(false); }
              }} disabled={savingProfile || usernameInput === user.username} className="mt-2 text-[12px] font-extrabold text-blue-600 disabled:opacity-50">Save username →</button>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> CHANGE PASSWORD</div>
              <input type="password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} placeholder="Current password" className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:border-blue-400" />
              <input type="password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} placeholder="New password (min 6 chars)" className="mt-2 w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-[14px] focus:outline-none focus:border-blue-400" />
              <button onClick={async () => {
                setSavingProfile(true);
                try { await authApi.changePassword({ current_password: pwdCurrent, new_password: pwdNew }); setPwdCurrent(''); setPwdNew(''); toast({ title: 'Password updated' }); }
                catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
                finally { setSavingProfile(false); }
              }} disabled={savingProfile || !pwdCurrent || pwdNew.length < 6} className="mt-3 w-full rounded-full py-3 font-extrabold text-white text-[13px] shadow-lg disabled:opacity-50" style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}>
                Update password
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-2">ACCOUNT INFO</div>
              <Info label="Email" value={user.email} />
              <Info label="Player ID" value={playerId} />
              <Info label="Wallet" value={user.walletAddress ? user.walletAddress.slice(0,8) + '...' + user.walletAddress.slice(-6) : 'Not linked'} />
              <Info label="Status" value={user.emailVerified ? 'Verified' : 'Unverified'} />
            </div>
          </div>
        )}

        {view === 'history' && (
          <div className="px-6 pb-6">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Transaction History</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Last 50 transactions</p>
            <div className="mt-4 space-y-2 max-h-[440px] overflow-y-auto pr-1">
              {txs == null ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
              ) : txs.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-[13px]">No transactions yet.</div>
              ) : txs.map((tx, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      tx.type?.includes('presale') ? 'bg-blue-100 text-blue-700' :
                      tx.type?.includes('airdrop') ? 'bg-amber-100 text-amber-700' :
                      tx.type?.includes('withdrawal') ? 'bg-red-100 text-red-700' :
                      tx.type?.includes('pvp') ? 'bg-purple-100 text-purple-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {tx.type?.includes('withdrawal') ? <ArrowUpFromLine className="w-4 h-4" /> : <ArrowDownToLine className="w-4 h-4" />}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-slate-900 truncate">{(tx.type || '').replace(/_/g, ' ').toUpperCase()}</div>
                      <div className="text-[11px] text-slate-500">{(tx.createdAt || '').slice(0, 16).replace('T', ' ')}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[14px] font-black text-slate-900">
                      {(tx.total_tokens || tx.amount_tokens || tx.amount_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-[10px] font-bold text-slate-500">{tx.amount_usd ? 'USD' : 'DOMIA'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === 'gameHistory' && (
          <div className="px-6 pb-6" data-testid="game-history-view">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Game History · Human vs AI</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">All your matches against the AI — wins, losses & tokens.</p>

            {gameHistory == null ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : (
              <>
                {/* Stats summary */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-center">
                    <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">WINS</div>
                    <div className="text-2xl font-black text-emerald-800 leading-none mt-0.5" data-testid="stats-wins">{gameHistory.stats?.wins || 0}</div>
                  </div>
                  <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2.5 text-center">
                    <div className="text-[10px] font-bold tracking-[0.14em] text-rose-700">LOSSES</div>
                    <div className="text-2xl font-black text-rose-800 leading-none mt-0.5" data-testid="stats-losses">{gameHistory.stats?.losses || 0}</div>
                  </div>
                </div>

                {/* Match list — clean, essentials only */}
                <div className="mt-4 text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-2">MATCH LOG · {gameHistory.items?.length || 0}</div>
                <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                  {(gameHistory.items?.length ?? 0) === 0 ? (
                    <div className="text-center py-10 text-slate-400 text-[13px]">No matches yet — play your first game! (Min bet: 500 DOMIA)</div>
                  ) : (gameHistory.items || []).map((m) => {
                    const isWin = m.result === 'win';
                    const resultColor = isWin ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200';
                    return (
                      <div key={m.match_id} className={`rounded-lg border px-3 py-2 flex items-center justify-between ${resultColor}`} data-testid={`match-row-${m.match_id}`}>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-[11px] font-black tracking-widest uppercase shrink-0">{m.game}</span>
                          <span className="text-[11px] font-extrabold uppercase">{isWin ? 'WIN' : 'LOSS'}</span>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[13px] font-black tabular-nums">
                            {isWin ? `+${Math.floor(m.credit).toLocaleString()}` : `-${Math.floor(m.bet).toLocaleString()}`} DOMIA
                          </div>
                          <div className="text-[10px] opacity-75">Bet: {Math.floor(m.bet).toLocaleString()} DOMIA</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {view === 'referral' && (
          <div className="px-6 pb-6" data-testid="referral-view">
            <button onClick={() => setView('main')} className="text-[12px] font-bold text-blue-600 mb-3">← Back</button>
            <h3 className="text-lg font-black text-slate-900">Programme de Parrainage</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Invitez vos amis et gagnez des DOMIA à chaque dépôt — sur 3 niveaux.</p>

            {!refInfo ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : (
              <>
                {/* Link */}
                <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <div className="text-[10px] font-bold tracking-[0.16em] text-blue-700">YOUR REFERRAL LINK</div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <input
                      readOnly
                      value={refInfo.link}
                      data-testid="referral-link-input"
                      className="flex-1 min-w-0 bg-white border border-blue-300 rounded-xl px-3 py-2.5 text-[12px] font-mono text-slate-900 focus:outline-none"
                    />
                    <button
                      data-testid="copy-referral-link-btn"
                      onClick={() => {
                        try { navigator.clipboard.writeText(refInfo.link); } catch (e) { /* ignore */ }
                        setRefCopied(true);
                        setTimeout(() => setRefCopied(false), 1800);
                        toast({ title: 'Lien copié !', description: refInfo.link });
                      }}
                      className="shrink-0 rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-3 py-2.5 text-[12px] font-extrabold flex items-center gap-1"
                    >
                      {refCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {refCopied ? 'Copié' : 'Copier'}
                    </button>
                  </div>

                  {/* Share buttons */}
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <a target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${refInfo.shortMessage} ${refInfo.link}`)}`} className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white py-2 text-[12px] font-extrabold flex items-center justify-center gap-1" data-testid="share-whatsapp">
                      WhatsApp
                    </a>
                    <a target="_blank" rel="noopener noreferrer" href={`https://t.me/share/url?url=${encodeURIComponent(refInfo.link)}&text=${encodeURIComponent(refInfo.shortMessage)}`} className="rounded-xl bg-sky-500 hover:bg-sky-600 text-white py-2 text-[12px] font-extrabold flex items-center justify-center gap-1" data-testid="share-telegram">
                      <Send className="w-3.5 h-3.5" /> Telegram
                    </a>
                    <a target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${refInfo.shortMessage} ${refInfo.link}`)}`} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white py-2 text-[12px] font-extrabold flex items-center justify-center gap-1" data-testid="share-twitter">
                      <Twitter className="w-3.5 h-3.5" /> X
                    </a>
                  </div>
                </div>

                {/* Referral counts summary — directs vs indirects (live from backend) */}
                <div className="mt-4 grid grid-cols-3 gap-2" data-testid="referral-summary">
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-center">
                    <div className="text-[10px] font-bold tracking-[0.14em] text-blue-700">DIRECTS</div>
                    <div className="text-2xl font-black text-blue-800 leading-none mt-0.5" data-testid="referral-directs-count">{refInfo.counts?.l1 || 0}</div>
                    <div className="text-[9px] text-blue-600 mt-0.5">Niveau 1</div>
                  </div>
                  <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-center">
                    <div className="text-[10px] font-bold tracking-[0.14em] text-violet-700">INDIRECTS</div>
                    <div className="text-2xl font-black text-violet-800 leading-none mt-0.5" data-testid="referral-indirects-count">{(refInfo.counts?.l2 || 0) + (refInfo.counts?.l3 || 0)}</div>
                    <div className="text-[9px] text-violet-600 mt-0.5">Niveaux 2+3</div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-center">
                    <div className="text-[10px] font-bold tracking-[0.14em] text-emerald-700">TOTAL</div>
                    <div className="text-2xl font-black text-emerald-800 leading-none mt-0.5" data-testid="referral-total-count">{refInfo.counts?.total || 0}</div>
                    <div className="text-[9px] text-emerald-600 mt-0.5">Réseau complet</div>
                  </div>
                </div>

                {/* Earnings per level */}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((lvl) => {
                    const pct = lvl === 1 ? '7%' : lvl === 2 ? '2%' : '1%';
                    const refKey = `l${lvl}`;
                    const earned = refInfo.earnings?.[refKey] || 0;
                    const count = refInfo.counts?.[refKey] || 0;
                    return (
                      <div key={lvl} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`level-${lvl}-card`}>
                        <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">LEVEL {lvl}</div>
                        <div className="mt-0.5 text-[10px] font-extrabold text-blue-700">{pct} commission</div>
                        <div className="mt-2 text-xl font-black text-slate-900 leading-none">{earned.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">DOMIA earned · {count} filleul{count > 1 ? 's' : ''}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-center justify-between">
                  <span><b>Total gagné</b> : commissions versées directement à ton solde DOMIA</span>
                  <span className="font-black text-emerald-700 text-[14px]">{(refInfo.earnings?.total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} DOMIA</span>
                </div>

                {/* Recent earnings */}
                <div className="mt-4">
                  <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-2">RECENT COMMISSIONS</div>
                  {refEarnings == null ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-blue-600" /></div>
                  ) : refEarnings.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-[12px]">Pas encore de commissions. Partage ton lien pour commencer !</div>
                  ) : (
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                      {refEarnings.map((e, i) => (
                        <div key={i} className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex items-center justify-between text-[12px]">
                          <div>
                            <div className="font-bold text-slate-800">Level {e.level} · {(e.rate * 100).toFixed(0)}%</div>
                            <div className="text-[10px] text-slate-500">{(e.createdAt || '').slice(0, 16).replace('T', ' ')}</div>
                          </div>
                          <div className="font-black text-emerald-700">+{e.amount_domia.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <AdminPage open={adminOpen} onClose={() => setAdminOpen(false)} />
      <WithdrawModal open={withdrawOpen} onClose={() => setWithdrawOpen(false)} />
      <PhantomDepositModal open={phantomDepositOpen} onClose={() => setPhantomDepositOpen(false)} />
    </>
  );
};

const Info = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[12px] text-slate-500">{label}</span>
    <span className="text-[12px] font-bold text-slate-900 truncate ml-2">{value}</span>
  </div>
);

export default Dashboard;
